(() => {
  const main = document.getElementById('MainContent');
  if (!main) return;
  const selector = '.ph-seasonal__grid,.ph-gifts__grid,.ph-speciality-gateway__grid,slider-component .product-grid,.related-products .product-grid';
  const compact = matchMedia('(max-width: 989px)');
  const states = new Map();
  let serial = 0;
  let queued = false;
  const pendingMeasurements = new Set();
  let measurementsQueued = false;
  const scheduleMeasurement = read => {
    pendingMeasurements.add(read);
    if (measurementsQueued) return;
    measurementsQueued = true;
    requestAnimationFrame(() => {
      measurementsQueued = false;
      const batch = [...pendingMeasurements];
      pendingMeasurements.clear();
      const commits = batch.map(measure => measure()).filter(Boolean);
      commits.forEach(commit => commit());
    });
  };
  let activeTouchCard = null;
  const touchCards = '.ph-retail-card,.product-card-wrapper';
  const clearTouchCard = () => {
    activeTouchCard?.classList.remove('is-touch-active');
    activeTouchCard = null;
  };
  const activateTouchCard = event => {
    const coarseInput = event.pointerType === 'touch' || event.pointerType === 'pen' || matchMedia('(hover:none) and (pointer:coarse)').matches;
    if (!coarseInput) { clearTouchCard(); return; }
    const card = event.target.closest(touchCards);
    if (!card || !main.contains(card)) { clearTouchCard(); return; }
    if (activeTouchCard !== card) clearTouchCard();
    activeTouchCard = card;
    card.classList.add('is-touch-active');
  };
  const shouldRail = rail => rail.children.length > 4 || compact.matches && rail.children.length === 4;
  const destroy = (rail, state) => {
    state.abort.abort();
    state.resize.disconnect();
    state.controls.remove();
    rail.classList.remove('ph-product-rail');
    delete rail.dataset.phRail;
    for (const [name, value] of Object.entries(state.original)) {
      if (value === null) rail.removeAttribute(name); else rail.setAttribute(name, value);
    }
    if (state.generatedId) rail.removeAttribute('id');
    states.delete(rail);
  };
  const setup = () => {
    main.dataset.phProductLayout = 'shelves';
    for (const [rail, state] of states) {
      if (!rail.isConnected || !shouldRail(rail)) destroy(rail, state);
    }
    main.querySelectorAll(selector).forEach(rail => {
      if (!shouldRail(rail)) return;
      if (states.has(rail)) { states.get(rail).update(); return; }
      const original = Object.fromEntries(['tabindex','aria-label','aria-roledescription'].map(name => [name, rail.getAttribute(name)]));
      const generatedId = !rail.id;
      if (generatedId) rail.id = `ph-product-rail-${++serial}`;
      rail.dataset.phRail = 'ready';
      rail.classList.add('ph-product-rail');
      const controls = document.createElement('div');
      controls.className = 'ph-rail-controls';
      const info = document.createElement('span');
      const prev = document.createElement('button');
      const next = document.createElement('button');
      prev.type = next.type = 'button';
      prev.textContent = '←'; next.textContent = '→';
      prev.setAttribute('aria-label', main.dataset.phRailPrevious);
      next.setAttribute('aria-label', main.dataset.phRailNext);
      [prev,next].forEach(button => button.setAttribute('aria-controls', rail.id));
      controls.append(info,prev,next);
      rail.after(controls);
      const abort = new AbortController();
      const update = () => scheduleMeasurement(measure);
      const measure = () => {
        if (!rail.isConnected || !states.has(rail)) return;
        const width = rail.clientWidth;
        const scrollWidth = rail.scrollWidth;
        const overflow = scrollWidth > width + 3;
        const rtl = getComputedStyle(rail).direction === 'rtl';
        const offset = Math.abs(rail.scrollLeft);
        const box = rail.getBoundingClientRect();
        const items = [...rail.children]
          .map(element => element.getBoundingClientRect())
          .filter(rect => rect.width > 0);
        const visible = items
          .map((rect, index) => ({ index: index + 1, rect }))
          .filter(item => item.rect.left < box.right - 5 && item.rect.right > box.left + 5);
        const position = visible.length
          ? main.dataset.phRailPosition.replace('[first]', visible[0].index).replace('[last]', visible.at(-1).index).replace('[total]', items.length)
          : '';
        return () => {
          controls.hidden = !overflow;
          if (overflow) {
            rail.tabIndex = 0;
            rail.setAttribute('aria-label', main.dataset.phRailLabel);
          } else {
            for (const name of ['tabindex', 'aria-label']) {
              if (original[name] === null) rail.removeAttribute(name);
              else rail.setAttribute(name, original[name]);
            }
          }
          prev.disabled = offset <= 2;
          next.disabled = offset + width >= scrollWidth - 3;
          const previousLabel = rtl ? '→' : '←';
          const nextLabel = rtl ? '←' : '→';
          if (prev.textContent !== previousLabel) prev.textContent = previousLabel;
          if (next.textContent !== nextLabel) next.textContent = nextLabel;
          if (info.textContent !== position) info.textContent = position;
        };
      };
      const move = direction => rail.scrollBy({left:direction * rail.clientWidth * (getComputedStyle(rail).direction === 'rtl' ? -1 : 1),behavior:matchMedia('(prefers-reduced-motion:reduce)').matches ? 'instant' : 'smooth'});
      prev.addEventListener('click',() => move(-1),{signal:abort.signal});
      next.addEventListener('click',() => move(1),{signal:abort.signal});
      rail.addEventListener('keydown',event => {
        if (event.target !== rail || !['ArrowRight','ArrowLeft'].includes(event.key)) return;
        event.preventDefault();
        const rtl = getComputedStyle(rail).direction === 'rtl';
        move((event.key === 'ArrowRight' ? 1 : -1) * (rtl ? -1 : 1));
      },{signal:abort.signal});
      rail.addEventListener('scroll',update,{passive:true,signal:abort.signal});
      const resize = new ResizeObserver(update);
      resize.observe(rail);
      states.set(rail,{abort,resize,controls,original,generatedId,update});
      update();
    });
  };
  const queue = () => {
    if (queued) return;
    queued = true;
    setTimeout(() => requestAnimationFrame(() => { queued = false; setup(); }), 160);
  };
  const observer = new MutationObserver(mutations => {
    const relevant = mutations.some(m => {
      if (m.target.closest && m.target.closest('.ph-rail-controls, .card, .card-wrapper, .ph-retail-card, .product-card-wrapper, product-form, .price')) return false;
      return [...m.addedNodes,...m.removedNodes].some(n => n.nodeType === 1 && !n.classList.contains('ph-rail-controls') && (n.matches(selector) || n.querySelector(selector) || (n.parentElement && n.parentElement.matches(selector))));
    });
    if (relevant) queue();
  });
  observer.observe(main,{childList:true,subtree:true});
  document.addEventListener('shopify:section:load',queue);
  document.addEventListener('shopify:section:unload',event => {
    for (const [rail,state] of states) if (event.target.contains(rail)) destroy(rail,state);
  });
  compact.addEventListener('change',queue);
  document.addEventListener('pointerdown',activateTouchCard,{passive:true,capture:true});
  document.addEventListener('keydown',clearTouchCard,{passive:true});
  window.addEventListener('pageshow',() => { clearTouchCard(); queue(); });
  setup();
})();

